/**
 * Field primitives for the planner form.
 *
 * Ported from the user platform's PortfolioPlanner/PlannerFields.tsx. Same
 * fields, same masking behaviour, same tags; the styling is the admin indigo
 * system rather than the client report identity, and ids come from `useAutoId`
 * because React 17 has no `useId`.
 */

import React from "react";
import {
  groupIndian,
  parseIndian,
} from "../../../Helpers/portfolioPlanner/format";
import PlannerSelect from "./PlannerSelect";
import { useAutoId } from "./useAutoId";
import styles from "./portfolio-planner.module.css";

export const InlineLoader = () => (
  <span className={styles.inlineSpinner} aria-hidden="true" />
);

interface FieldShellProps {
  label: string;
  /** Small tag beside the label — "auto", "linked", "suggested". */
  tag?: string;
  hint?: string;
  children: React.ReactNode;
  className?: string;
  /**
   * A <label> wrapping the custom listbox would forward its click to the
   * trigger and toggle the popup straight back shut, so that one field renders
   * a plain div and associates its label through aria-labelledby instead.
   */
  as?: "label" | "div";
  labelId?: string;
}

export const FieldShell = ({
  label,
  tag,
  hint,
  children,
  className,
  as: Element = "label",
  labelId,
}: FieldShellProps) => (
  <Element className={`${styles.field} ${className || ""}`}>
    <span className={styles.fieldLabel} id={labelId}>
      {label}
      {tag ? <i className={styles.fieldTag}>{tag}</i> : null}
    </span>
    {children}
    {hint ? <small className={styles.fieldHint}>{hint}</small> : null}
  </Element>
);

/**
 * Currency input that masks to Indian grouping as the caller types while
 * keeping the raw number. The displayed string is derived from the value, so
 * the two can never drift apart.
 */
export const MoneyField = ({
  label,
  value,
  onChange,
  placeholder,
  tag,
  hint,
  className,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  placeholder?: string;
  tag?: string;
  hint?: string;
  className?: string;
}) => (
  <FieldShell label={label} tag={tag} hint={hint} className={className}>
    <div className={styles.moneyWrap}>
      <i className={styles.moneyPrefix} aria-hidden="true">
        &#8377;
      </i>
      <input
        type="text"
        inputMode="numeric"
        value={value ? groupIndian(String(value)) : ""}
        placeholder={placeholder}
        onChange={(event) => onChange(parseIndian(event.target.value))}
      />
    </div>
  </FieldShell>
);

export const TextField = ({
  label,
  value,
  onChange,
  placeholder,
  tag,
  hint,
  className,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  tag?: string;
  hint?: string;
  className?: string;
}) => (
  <FieldShell label={label} tag={tag} hint={hint} className={className}>
    <input
      type="text"
      value={value}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
    />
  </FieldShell>
);

export const NumberField = ({
  label,
  value,
  onChange,
  placeholder,
  min,
  max,
  step,
  suffix,
  tag,
  hint,
  className,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  tag?: string;
  hint?: string;
  className?: string;
}) => (
  <FieldShell label={label} tag={tag} hint={hint} className={className}>
    <div className={suffix ? styles.suffixWrap : undefined}>
      <input
        type="number"
        value={value}
        placeholder={placeholder}
        min={min}
        max={max}
        step={step}
        onChange={(event) => onChange(event.target.value)}
      />
      {suffix ? (
        <i className={styles.fieldSuffix} aria-hidden="true">
          {suffix}
        </i>
      ) : null}
    </div>
  </FieldShell>
);

/**
 * Years, months and ages: whole numbers only. A keystroke that would make the
 * value anything but digits is dropped rather than stripped — stripping would
 * turn a pasted "8.3" into 83. `type="number"` cannot do this: its min, max and
 * step only steer the spinner, and "8.3" or "15" still reach the form.
 */
export const WholeNumberField = ({
  label,
  value,
  onChange,
  placeholder,
  maxLength = 3,
  suffix,
  tag,
  hint,
  className,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  suffix?: string;
  tag?: string;
  hint?: string;
  className?: string;
}) => (
  <FieldShell label={label} tag={tag} hint={hint} className={className}>
    <div className={suffix ? styles.suffixWrap : undefined}>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        onChange={(event) => {
          const next = event.target.value;
          if (/^\d*$/.test(next)) onChange(next);
        }}
      />
      {suffix ? (
        <i className={styles.fieldSuffix} aria-hidden="true">
          {suffix}
        </i>
      ) : null}
    </div>
  </FieldShell>
);

export const DateField = ({
  label,
  value,
  onChange,
  min,
  tag,
  hint,
  className,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  tag?: string;
  hint?: string;
  className?: string;
}) => (
  <FieldShell label={label} tag={tag} hint={hint} className={className}>
    <input
      type="date"
      value={value}
      min={min}
      onChange={(event) => onChange(event.target.value)}
    />
  </FieldShell>
);

export function SelectField<T extends string | number>({
  label,
  value,
  options,
  onChange,
  formatOption,
  tag,
  hint,
  className,
}: {
  label: string;
  value: T;
  options: ReadonlyArray<T>;
  onChange: (value: T) => void;
  formatOption?: (option: T) => string;
  tag?: string;
  hint?: string;
  className?: string;
}) {
  const labelId = useAutoId("planner-label");
  return (
    <FieldShell
      as="div"
      labelId={labelId}
      label={label}
      tag={tag}
      hint={hint}
      className={className}
    >
      <PlannerSelect
        value={value}
        options={options}
        onChange={onChange}
        formatOption={formatOption}
        labelledBy={labelId}
      />
    </FieldShell>
  );
}

/** Segmented pair of cards — risk profile, investor experience. */
export function ChoiceField<T extends string>({
  label,
  value,
  options,
  onChange,
  wide = true,
}: {
  label: string;
  value: T | null;
  options: { value: T; title: string; detail?: string }[];
  onChange: (value: T) => void;
  wide?: boolean;
}) {
  return (
    <div className={wide ? styles.choiceWide : undefined}>
      <span className={styles.fieldLabel}>{label}</span>
      <div className={styles.choiceGrid}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`${styles.choice} ${
              value === option.value ? styles.choiceActive : ""
            }`}
            aria-pressed={value === option.value}
            onClick={() => onChange(option.value)}
          >
            {option.title}
            {option.detail ? (
              <small className={styles.choiceDetail}>{option.detail}</small>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  );
}

export const ToggleField = ({
  label,
  checked,
  onChange,
  disabled,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  hint?: string;
}) => {
  const id = useAutoId("planner-toggle");
  return (
    <div className={styles.toggleWrap}>
      <span className={styles.toggleLine}>
        <label
          className={`${styles.toggle} ${disabled ? styles.toggleDisabled : ""}`}
          htmlFor={id}
        >
          <input
            id={id}
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={(event) => onChange(event.target.checked)}
          />
          <span>{label}</span>
        </label>
      </span>
      {hint ? <small className={styles.fieldHint}>{hint}</small> : null}
    </div>
  );
};

/** The live pill row under a goal or liability row. */
export const BadgeRow = ({
  badges,
}: {
  badges: { text: string; tone?: "neutral" | "good" | "warn" | "accent" }[];
}) => (
  <div className={styles.badgeRow}>
    {badges.map((badge) => (
      <span
        key={badge.text}
        className={`${styles.badge} ${
          badge.tone === "good"
            ? styles.badgeGood
            : badge.tone === "warn"
            ? styles.badgeWarn
            : badge.tone === "accent"
            ? styles.badgeAccent
            : ""
        }`}
      >
        {badge.text}
      </span>
    ))}
  </div>
);
